/**
 * Authentication Context
 *
 * Provides authentication state and methods using Supabase Magic Link.
 * Wraps both website and CAD app routes to provide auth state to all components.
 *
 * Profile creation is handled by a DB trigger on auth.users INSERT.
 * LoginPage passes display_name + preferences via options.data in signInWithOtp,
 * which writes to raw_user_meta_data. The trigger reads that metadata and
 * auto-creates the user_profiles row server-side.
 */

import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from "react";
import { User, Session, AuthError } from "@supabase/supabase-js";
import { supabase, isSupabaseConfigured } from "../supabase";
import { debugLog } from "@/core/utils/debug";
import { getProfile, updateProfile } from "../supabase/profileService";
import type { UserProfile } from "../supabase/types";
import { supabaseAuthService } from "../services/supabaseAuthService";

const HAS_ACCOUNT_KEY = "stagecraft_has_account";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: UserProfile | null;
  loading: boolean;
  isAuthenticated: boolean;
  hasAccount: boolean;
  isConfigured: boolean;
  signInWithMagicLink: (
    email: string,
    metadata?: Record<string, string>,
  ) => Promise<{ error: AuthError | null }>;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: React.ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [hasAccount, setHasAccount] = useState(
    () => localStorage.getItem(HAS_ACCOUNT_KEY) === "1",
  );
  const isConfigured = isSupabaseConfigured();

  /**
   * Load user profile from database
   */
  const loadProfile = useCallback(async (userId: string) => {
    const userProfile = await getProfile(userId);
    setProfile(userProfile);
    return userProfile;
  }, []);

  /**
   * Refresh the current user's profile
   */
  const refreshProfile = useCallback(async () => {
    if (user?.id) {
      await loadProfile(user.id);
    }
  }, [user?.id, loadProfile]);

  /**
   * Handle sign-in via the SIGNED_IN event listener path.
   * Loads the profile and patches display_name from user_metadata if needed.
   * Note: initializeAuth also does this patching as belt-and-suspenders.
   */
  const handleNewUserSignup = useCallback(
    async (
      userId: string,
      _userEmail: string,
      userMetadata?: Record<string, any>,
    ) => {
      const existingProfile = await getProfile(userId);
      if (existingProfile) {
        debugLog("Profile loaded for user:", userId);

        // Patch profile with signup metadata if the user provided a display_name
        // during signup. The DB trigger creates the profile before metadata is written,
        // so it falls back to the email prefix. We patch it here with the real name.
        if (
          userMetadata?.display_name &&
          existingProfile.display_name !== userMetadata.display_name
        ) {
          debugLog("Patching profile with signup metadata");
          await updateProfile(userId, {
            display_name: userMetadata.display_name,
            email_preference:
              userMetadata.email_preference || existingProfile.email_preference,
          });
          // Reload to get the updated profile
          const updated = await getProfile(userId);
          setProfile(updated);
        } else {
          setProfile(existingProfile);
        }
      } else {
        debugLog(
          "Profile not found after sign-in, will be created by donorService",
        );
      }
    },
    [],
  );

  // Initialize auth state on mount — delegates to supabaseAuthService singleton
  useEffect(() => {
    if (!isConfigured || !supabase) {
      setLoading(false);
      return;
    }

    const initializeAuth = async () => {
      try {
        // Ensure the singleton is initialized (idempotent)
        await supabaseAuthService.initialize();

        // Read current session from the singleton
        const currentSession = supabaseAuthService.getSession();
        setSession(currentSession);
        setUser(currentSession?.user ?? null);

        // Load profile if user is logged in
        if (currentSession?.user) {
          localStorage.setItem(HAS_ACCOUNT_KEY, "1");
          setHasAccount(true);

          // Load the profile from DB
          let existingProfile = await loadProfile(currentSession.user.id);

          // If profile is null, the DB trigger may not have committed yet.
          // Retry once after a short delay.
          if (!existingProfile) {
            debugLog("initializeAuth: Profile not found, retrying in 1s...");
            await new Promise((resolve) => setTimeout(resolve, 1000));
            existingProfile = await loadProfile(currentSession.user.id);
          }

          // Metadata patching: The DB trigger creates the profile before
          // raw_user_meta_data is written, so display_name falls back to
          // the email prefix. If the session's user_metadata has a
          // display_name that differs from the profile, patch it now.
          const userMetadata = currentSession.user.user_metadata;
          if (
            existingProfile &&
            userMetadata?.display_name &&
            existingProfile.display_name !== userMetadata.display_name
          ) {
            debugLog(
              "initializeAuth: Patching profile display_name from metadata",
            );
            await updateProfile(currentSession.user.id, {
              display_name: userMetadata.display_name,
              email_preference:
                userMetadata.email_preference ||
                existingProfile.email_preference,
            });
            // Reload to get the patched profile into state
            await loadProfile(currentSession.user.id);
          }
        }
      } catch (error) {
        console.error("Error initializing auth:", error);
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();

    // Listen for auth state changes via the singleton
    const unsubscribe = supabaseAuthService.onAuthStateChange(
      async (event, newSession) => {
        debugLog("AuthContext: Auth state changed:", event);
        setSession(newSession);
        setUser(newSession?.user ?? null);

        if (event === "SIGNED_IN" && newSession?.user) {
          debugLog("AuthContext: User signed in:", newSession.user.id);
          localStorage.setItem(HAS_ACCOUNT_KEY, "1");
          setHasAccount(true);
          await handleNewUserSignup(
            newSession.user.id,
            newSession.user.email || "",
            newSession.user.user_metadata,
          );
        } else if (event === "SIGNED_OUT") {
          debugLog("AuthContext: User signed out");
          setProfile(null);
        }

        setLoading(false);
      },
    );

    return () => {
      unsubscribe();
    };
  }, [isConfigured, loadProfile, handleNewUserSignup]);

  /**
   * Sign in with Magic Link — delegates to singleton
   */
  const signInWithMagicLink = useCallback(
    async (email: string, metadata?: Record<string, string>) => {
      if (!isConfigured || !supabase) {
        return { error: { message: "Supabase not configured" } as AuthError };
      }

      const result = await supabaseAuthService.sendMagicLink(email, metadata);
      if (!result.success) {
        return {
          error: {
            message: result.error || "Failed to send magic link",
          } as AuthError,
        };
      }

      return { error: null };
    },
    [isConfigured],
  );

  /**
   * Sign out — delegates to singleton
   */
  const signOut = useCallback(async () => {
    await supabaseAuthService.signOut();
  }, []);

  const value: AuthContextType = {
    user,
    session,
    profile,
    loading,
    isAuthenticated: !!session,
    hasAccount,
    isConfigured,
    signInWithMagicLink,
    signOut,
    refreshProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Hook to access auth context — works in both website and CAD app routes
 */
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
